import { useDashboardFollowups } from '../../hooks/useDashboard';

interface Props {
  filters: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  };
}

const DECISION_LABELS: Record<string, string> = {
  CLOSURE: 'Closures follow-up',
  FINES: 'Fines follow-up',
  WARNING: 'Warnings follow-up',
};

const DECISION_COLORS: Record<string, string> = {
  CLOSURE: 'var(--nhip-red)',
  FINES: '#F59E0B',
  WARNING: '#6B7280',
};

function DecisionBar({ type, info }: { type: string; info: { due: number; carried: number; completionRate: number } }) {
  const pct = Math.min(100, Math.max(0, info.completionRate));
  const color = DECISION_COLORS[type] ?? 'var(--nhip-green)';

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center justify-between'>
        <span className='text-xs font-medium' style={{ color: 'var(--nhip-ink)' }}>
          {DECISION_LABELS[type] ?? type}
        </span>
        <span className='text-xs font-semibold' style={{ color }}>
          {pct.toFixed(0)}%
        </span>
      </div>
      <div className='h-2 rounded-full overflow-hidden' style={{ backgroundColor: 'var(--nhip-border)' }}>
        <div
          className='h-full rounded-full transition-all duration-500'
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className='text-[10px]' style={{ color: 'var(--nhip-ink-muted)' }}>
        {info.carried} of {info.due} completed
      </span>
    </div>
  );
}

export default function FollowupCompletion({ filters }: Props) {
  const { data, isLoading } = useDashboardFollowups(filters);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const overallPct: number = raw?.completionRate ?? 0;
  const totalDue: number = raw?.totalDue ?? 0;
  const totalCarried: number = raw?.totalCarried ?? 0;
  const byDecisionType: Record<string, { due: number; carried: number; completionRate: number }> = raw?.byDecisionType ?? {};
  const decisionEntries = Object.entries(byDecisionType);

  return (
    <div
      className='bg-white rounded-xl overflow-hidden flex flex-col'
      style={{ border: '1px solid var(--nhip-border)' }}
    >
      <div className='px-4 py-3 border-b flex items-center justify-between' style={{ borderColor: 'var(--nhip-border)' }}>
        <h2 className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>
          Follow-up Completion
        </h2>
        {raw?.period && (
          <span className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>{raw.period}</span>
        )}
      </div>

      <div className='p-4 flex flex-col gap-4'>
        {isLoading ? (
          <div className='flex flex-col gap-3 animate-pulse'>
            <div className='h-12 w-20 rounded mx-auto' style={{ backgroundColor: 'var(--nhip-border)' }} />
            <div className='space-y-3'>
              {[1, 2, 3].map(i => (
                <div key={i} className='space-y-1'>
                  <div className='h-3 w-32 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
                  <div className='h-2 w-full rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
                </div>
              ))}
            </div>
          </div>
        ) : !data ? (
          <p className='text-xs text-center py-4' style={{ color: 'var(--nhip-ink-muted)' }}>No data available</p>
        ) : (
          <>
            {/* Big percentage */}
            <div className='flex flex-col items-center gap-1'>
              <span
                className='text-5xl font-bold tracking-tight'
                style={{ color: overallPct >= 60 ? 'var(--nhip-green)' : overallPct >= 30 ? '#F59E0B' : 'var(--nhip-red)' }}
              >
                {overallPct.toFixed(0)}%
              </span>
              <span className='text-xs font-medium' style={{ color: 'var(--nhip-ink-muted)' }}>
                Overall completion rate
              </span>
              <span className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                {totalCarried} / {totalDue} follow-ups completed
              </span>
              {(raw?.totalOverdue ?? 0) > 0 && (
                <span className='text-xs font-semibold mt-0.5' style={{ color: 'var(--nhip-red)' }}>
                  {raw.totalOverdue} overdue
                </span>
              )}
            </div>

            <div className='border-t' style={{ borderColor: 'var(--nhip-border)' }} />

            {/* By decision type */}
            {decisionEntries.length > 0 && (
              <div className='flex flex-col gap-3'>
                <p className='text-[10px] font-semibold uppercase tracking-wider' style={{ color: 'var(--nhip-ink-muted)' }}>
                  By Decision Type
                </p>
                {decisionEntries.map(([type, info]) => (
                  <DecisionBar key={type} type={type} info={info} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
